import { ArrowDown, Eye, EyeOff, Mic, MicOff, Monitor, MonitorOff, Pause, Play, Video, VideoOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ReactNode } from "react";

interface SessionControlsProps {
  isMuted: boolean;
  onToggleMute: () => void;
  isPaused?: boolean;
  onTogglePause?: () => void;
  onEnd?: () => void;
  /** Optional camera toggle (Tavus only — Heygen has no self-camera). */
  isCameraOn?: boolean;
  onToggleCamera?: () => void;
  /** Optional screen-share toggle (Tavus only). */
  isScreenSharing?: boolean;
  onToggleScreenShare?: () => void;
  /**
   * Optional self-view hide toggle. Only shown when the user's camera is
   * on. Hiding is purely visual; the camera track keeps publishing.
   */
  isSelfViewHidden?: boolean;
  onToggleSelfView?: () => void;
  /** Compact sizing for tiny surfaces (e.g. inside a small bubble). */
  compact?: boolean;
  className?: string;
}

/**
 * Mic / pause / end button row used both inside `AvatarVideoArea` and
 * standalone (e.g. for the document-with-bubble layout where controls live
 * outside the bubble at the center-bottom of the screen).
 */
export function SessionControls({
  isMuted, onToggleMute, isPaused = false, onTogglePause, onEnd,
  isCameraOn = false, onToggleCamera,
  isScreenSharing = false, onToggleScreenShare,
  isSelfViewHidden = false, onToggleSelfView,
  compact = false, className,
}: SessionControlsProps) {
  const wrapTip = (label: string, node: ReactNode) => (
    <Tooltip>
      <TooltipTrigger asChild>{node}</TooltipTrigger>
      <TooltipContent className="max-w-[220px] bg-popover text-popover-foreground border">
        {label}
      </TooltipContent>
    </Tooltip>
  );
  return (
    <TooltipProvider delayDuration={200}>
    <div className={cn("flex items-center", compact ? "gap-2" : "gap-4", className)}>
      <div className="relative">
        {isMuted && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 flex flex-col items-center animate-bounce pointer-events-none">
            <span
              className={cn(
                "rounded-full bg-destructive font-semibold text-destructive-foreground shadow-lg whitespace-nowrap",
                compact ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs",
              )}
            >
              Turn on microphone
            </span>
            <ArrowDown className={cn("text-destructive", compact ? "h-3 w-3" : "h-4 w-4")} />
          </div>
        )}
        {wrapTip(
          isMuted
            ? "Turn microphone on so the avatar can hear you."
            : "Mute your microphone. The avatar won't hear you.",
          <Button
            onClick={onToggleMute}
            variant={isMuted ? "destructive" : "secondary"}
            size="icon"
            className={cn("rounded-full shadow-lg", compact ? "h-10 w-10" : "h-14 w-14")}
            aria-label={isMuted ? "Turn microphone on" : "Mute microphone"}
          >
            {isMuted
              ? <MicOff className={compact ? "h-4 w-4" : "h-6 w-6"} />
              : <Mic className={compact ? "h-4 w-4" : "h-6 w-6"} />}
          </Button>,
        )}
      </div>
      {onToggleCamera && wrapTip(
        isCameraOn
          ? "Turn your camera off. Your self-view will disappear."
          : "Turn your camera on. You'll appear in a small self-view.",
        <Button
          onClick={onToggleCamera}
          variant={isCameraOn ? "default" : "secondary"}
          size="icon"
          className={cn("rounded-full shadow-lg", compact ? "h-10 w-10" : "h-14 w-14")}
          aria-label={isCameraOn ? "Turn camera off" : "Turn camera on"}
        >
          {isCameraOn
            ? <Video className={compact ? "h-4 w-4" : "h-6 w-6"} />
            : <VideoOff className={compact ? "h-4 w-4" : "h-6 w-6"} />}
        </Button>,
      )}
      {onToggleSelfView && isCameraOn && wrapTip(
        isSelfViewHidden
          ? "Show your self-view again."
          : "Hide your self-view. Your camera keeps streaming.",
        <Button
          onClick={onToggleSelfView}
          variant="secondary"
          size="icon"
          className={cn("rounded-full shadow-lg", compact ? "h-10 w-10" : "h-14 w-14")}
          aria-label={isSelfViewHidden ? "Show self view" : "Hide self view"}
        >
          {isSelfViewHidden
            ? <Eye className={compact ? "h-4 w-4" : "h-6 w-6"} />
            : <EyeOff className={compact ? "h-4 w-4" : "h-6 w-6"} />}
        </Button>,
      )}
      {onToggleScreenShare && wrapTip(
        isScreenSharing
          ? "Stop sharing your screen."
          : "Share a window, tab or your entire screen with the avatar.",
        <Button
          onClick={onToggleScreenShare}
          variant={isScreenSharing ? "default" : "secondary"}
          size="icon"
          className={cn("rounded-full shadow-lg", compact ? "h-10 w-10" : "h-14 w-14")}
          aria-label={isScreenSharing ? "Stop sharing screen" : "Share screen"}
        >
          {isScreenSharing
            ? <MonitorOff className={compact ? "h-4 w-4" : "h-6 w-6"} />
            : <Monitor className={compact ? "h-4 w-4" : "h-6 w-6"} />}
        </Button>,
      )}
      {onTogglePause && wrapTip(
        isPaused
          ? "Resume the session."
          : "Pause the session. The avatar will stop talking until you resume.",
        <Button
          onClick={onTogglePause}
          variant={isPaused ? "default" : "secondary"}
          size="icon"
          className={cn("rounded-full shadow-lg", compact ? "h-10 w-10" : "h-14 w-14")}
          aria-label={isPaused ? "Resume session" : "Pause session"}
        >
          {isPaused
            ? <Play className={compact ? "h-4 w-4" : "h-6 w-6"} />
            : <Pause className={compact ? "h-4 w-4" : "h-6 w-6"} />}
        </Button>,
      )}
      {onEnd && wrapTip(
        "End the session now. You can't rejoin.",
        <Button
          onClick={onEnd}
          variant="destructive"
          size={compact ? "sm" : "lg"}
          className={cn(
            "rounded-full font-bold uppercase tracking-wide shadow-lg",
            compact ? "h-10 px-3 text-xs" : "h-14 px-6 text-sm",
          )}
          aria-label="End session"
        >
          End
        </Button>,
      )}
    </div>
    </TooltipProvider>
  );
}